import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { FactList, PageIntro, Section } from '@/components/PageLayout'

const steps = [
  {
    title: 'The firm sources and underwrites',
    body: 'Properties are found, inspected, and modeled under the downside case before any investor sees them.',
  },
  {
    title: 'You review the offering',
    body: 'Each opportunity comes with the underwriting model, comparable sales and rents, a risk assessment, and the firm’s own view of what could go wrong. You decide on your own schedule.',
  },
  {
    title: 'The firm operates the property',
    body: 'Acquisition, renovation, leasing, and oversight of the property manager are handled by the firm. You receive quarterly reports and distributions.',
  },
]

export default function InvestPage() {
  return (
    <div className="bg-background text-foreground">
      <PageIntro title="Investing with Fairmeadow">
        <p>
          Investors provide capital to a specific property. The firm acquires, renovates, and oversees it, and reports
          each quarter.
        </p>
      </PageIntro>

      <Section title="Roles">
        <div className="grid gap-10 sm:grid-cols-2">
          <div>
            <h3 className="text-navy mb-3 font-semibold">The investor</h3>
            <ul className="text-foreground/85 border-navy space-y-0 border-t">
              {['Provides capital to a specific property', 'Reviews quarterly reports', 'Receives distributions'].map(
                (t) => (
                  <li key={t} className="border-border border-b py-3">
                    {t}
                  </li>
                )
              )}
            </ul>
          </div>
          <div>
            <h3 className="text-navy mb-3 font-semibold">The firm</h3>
            <ul className="text-foreground/85 border-navy space-y-0 border-t">
              {[
                'Sources and underwrites every acquisition',
                'Manages renovation and oversees operations',
                'Prepares quarterly reports and pays distributions',
              ].map((t) => (
                <li key={t} className="border-border border-b py-3">
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section title="Process">
        <ol>
          {steps.map((s, i) => (
            <li
              key={s.title}
              className="border-border grid grid-cols-[2.5rem_1fr] gap-x-4 border-t py-6 first:border-t-0 first:pt-0"
            >
              <span className="text-navy font-serif text-[1.75rem] leading-none" aria-hidden="true">
                {i + 1}
              </span>
              <div>
                <h3 className="text-navy mb-2 font-semibold">{s.title}</h3>
                <p className="text-foreground/85 max-w-xl leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section title="Typical terms" tinted>
        <FactList
          className="max-w-2xl"
          rows={[
            ['Investment per property', '$25,000 – $100,000'],
            ['Hold period', '5–10 years or longer'],
            ['Target cash-on-cash return', '8–12%, at current rents'],
            ['Distributions', 'Quarterly, once a property is stabilized'],
            ['Reporting', 'Quarterly, per property'],
          ]}
        />
        <p className="text-muted-foreground mt-6 max-w-2xl text-sm leading-relaxed">
          Terms for each opportunity are set out in its offering documents and may differ from the typical terms above.
          Target returns are projections, not guarantees.
        </p>
      </Section>

      <section>
        <div className="mx-auto flex max-w-5xl flex-col gap-6 px-5 py-14 md:flex-row md:items-center md:justify-between md:px-8">
          <p className="text-fluid-h3 text-navy max-w-xl">
            Not sure the structure suits you? The fit questionnaire takes about five minutes.
          </p>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Button asChild size="lg">
              <Link to="/contact">Join the investor list</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/fit">Take the questionnaire</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
